/*
 * voice-post-filter.js — Wiener-гейт по полосе голоса поверх разделителя.
 *
 * Разделитель оставляет в «не голосе» не копию голоса, а его ИСКАЖЁННЫЙ след
 * (voc − v): линейно он почти ортогонален голосу — проекция говорит «голос
 * вырезан на 60–95 дБ», — но разборчив, и там, где под ним тихо, слово
 * слышно. Гейт на каждой клетке (кадр, частота) полосы 300–4000 Гц гасит «не
 * голос» пропорционально доле голоса в этой клетке: attn = 1 − α·r²,
 * r = |V|²/(|V|² + |I|²). Что снято с «не голоса», прибавляется к голосу
 * (фазово-когерентно, в комплексной области) — сумма стемов не меняется.
 *
 * Замер 24.09.2026 (sepbench/gamevoice/variants.py: живая русская речь с
 * матом поверх звука восьми свободных игр, игра на 6 дБ тише голоса, 98 фраз,
 * судья — наш же распознаватель GigaAM): мат после Kim_Vocal_2 распознаётся в
 * 9 фразах из 98; Kim + этот гейт (α 1.0) — в 0 из 98, при α 0.6 — в 5; цена
 * для игры — 0.5 дБ (урон −11.3 → −10.8 дБ). Второй проход Kim — 4 из 98 за
 * удвоенное время, среднее Kim и HTDemucs — 7 из 98.
 *
 * Тот же алгоритм, что `aggressivePostFilter` в vocal-remover.blade.php и
 * `post_filter` в sepbench/bench_removeword.py (оттуда числа выше): STFT 4096 /
 * шаг 1024, окно Ханна, r в квадрате. Здесь — чистый модуль без страницы:
 * массивы каналов на вход, новые массивы на выход. Сверка с эталоном —
 * tests/js/profanity-voice-post-filter.test.mjs.
 */
(function (w) {
    'use strict';

    var N = 4096, HOP = 1024, HALF = N >> 1;
    var WIN = new Float64Array(N);
    for (var i = 0; i < N; i++) WIN[i] = 0.5 * (1 - Math.cos(2 * Math.PI * i / N));

    // перестановка и повороты — один раз на модуль
    var REV = new Uint32Array(N), COS = new Float64Array(HALF), SIN = new Float64Array(HALF);
    (function () {
        var bits = Math.round(Math.log(N) / Math.LN2);
        for (var i = 0; i < N; i++) {
            var r = 0;
            for (var b = 0; b < bits; b++) r |= ((i >> b) & 1) << (bits - 1 - b);
            REV[i] = r;
        }
        for (var k = 0; k < HALF; k++) { COS[k] = Math.cos(-2 * Math.PI * k / N); SIN[k] = Math.sin(-2 * Math.PI * k / N); }
    })();

    function fft(re, im, inverse) {
        for (var i = 0; i < N; i++) {
            var j = REV[i];
            if (j > i) { var t = re[i]; re[i] = re[j]; re[j] = t; t = im[i]; im[i] = im[j]; im[j] = t; }
        }
        for (var size = 2; size <= N; size <<= 1) {
            var halfSize = size >> 1, step = N / size;
            for (var start = 0; start < N; start += size) {
                for (var k = 0; k < halfSize; k++) {
                    var c = COS[k * step], s = inverse ? -SIN[k * step] : SIN[k * step];
                    var a = start + k, b = a + halfSize;
                    var tr = re[b] * c - im[b] * s, ti = re[b] * s + im[b] * c;
                    re[b] = re[a] - tr; im[b] = im[a] - ti;
                    re[a] += tr; im[a] += ti;
                }
            }
        }
        if (inverse) for (var q = 0; q < N; q++) { re[q] /= N; im[q] /= N; }
    }

    /* inst, vocal — массивы каналов (Float32Array) одной длины; sr — частота.
       opts: bandLow (300), bandHigh (4000), alpha (1.0). Возвращает новые
       { inst, vocal }; входы не трогает. */
    function apply(inst, vocal, sr, opts) {
        opts = opts || {};
        var bandLow = opts.bandLow != null ? opts.bandLow : 300;
        var bandHigh = opts.bandHigh != null ? opts.bandHigh : 4000;
        var alpha = opts.alpha != null ? opts.alpha : 1.0;
        var fLow = Math.max(1, Math.floor(bandLow * N / sr));
        var fHi = Math.min(HALF, Math.ceil(bandHigh * N / sr));
        var len = inst[0].length;
        // кадры как у эталона: от 0 с шагом HOP, хвост добит нулями
        var frames = Math.ceil(len / HOP) + 3;
        var wsum = new Float64Array(len);
        for (var f0 = 0; f0 < frames; f0++) {
            for (var n0 = 0; n0 < N; n0++) {
                var p0 = f0 * HOP + n0;
                if (p0 < len) wsum[p0] += WIN[n0] * WIN[n0];
            }
        }
        var iR = new Float64Array(N), iI = new Float64Array(N), vR = new Float64Array(N), vI = new Float64Array(N);
        var outI = [], outV = [];
        for (var ch = 0; ch < inst.length; ch++) {
            var xi = inst[ch], xv = vocal[ch];
            var yi = new Float64Array(len), yv = new Float64Array(len);
            for (var f = 0; f < frames; f++) {
                var off = f * HOP;
                for (var n = 0; n < N; n++) {
                    var p = off + n, wn = WIN[n];
                    iR[n] = p < len ? xi[p] * wn : 0; iI[n] = 0;
                    vR[n] = p < len ? xv[p] * wn : 0; vI[n] = 0;
                }
                fft(iR, iI, false);
                fft(vR, vI, false);
                for (var k = fLow; k <= fHi; k++) {
                    var im = iR[k] * iR[k] + iI[k] * iI[k];
                    var vm = vR[k] * vR[k] + vI[k] * vI[k];
                    var r = vm / (vm + im + 1e-20);
                    var attn = 1 - alpha * r * r;
                    if (attn < 0) attn = 0;
                    var nr = iR[k] * attn, ni = iI[k] * attn;
                    vR[k] += iR[k] - nr; vI[k] += iI[k] - ni;
                    iR[k] = nr; iI[k] = ni;
                    if (k > 0 && k < HALF) {
                        var k2 = N - k;
                        iR[k2] = nr; iI[k2] = -ni;
                        vR[k2] = vR[k]; vI[k2] = -vI[k];
                    }
                }
                fft(iR, iI, true);
                fft(vR, vI, true);
                for (var n2 = 0; n2 < N; n2++) {
                    var p2 = off + n2;
                    if (p2 >= len) break;
                    yi[p2] += iR[n2] * WIN[n2];
                    yv[p2] += vR[n2] * WIN[n2];
                }
            }
            var oi = new Float32Array(len), ov = new Float32Array(len);
            for (var q = 0; q < len; q++) {
                var ws = wsum[q] > 1e-8 ? 1 / wsum[q] : 0;
                oi[q] = yi[q] * ws; ov[q] = yv[q] * ws;
            }
            outI.push(oi); outV.push(ov);
        }
        return { inst: outI, vocal: outV };
    }

    w.VoicePostFilter = { apply: apply, N: N, HOP: HOP };
    if (typeof module !== 'undefined' && module.exports) module.exports = w.VoicePostFilter;
})(typeof window !== 'undefined' ? window : globalThis);
